(() => {
  if (window.sylavieHowWeMakeItControllerLoaded) return;

  window.sylavieHowWeMakeItControllerLoaded = true;

  const SELECTORS = {
    section: '[data-how-we-make-it]',
    timeline: '[data-how-we-make-it-timeline]',
    line: '.how-we-make-it__line',
    step: '[data-how-we-make-it-step]',
    marker: '[data-timeline-marker]',
    content: '[data-how-we-make-it-content]',
    media: '[data-how-we-make-it-media]',
  };

  const VISIBLE_CLASS = 'is-visible';
  const ACTIVE_CLASS = 'is-active';
  const instances = new WeakMap();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class HowWeMakeIt {
    constructor(section) {
      this.section = section;
      this.timeline = section.querySelector(SELECTORS.timeline);
      this.line = section.querySelector(SELECTORS.line);
      this.steps = Array.from(section.querySelectorAll(SELECTORS.step));
      this.markers = Array.from(section.querySelectorAll(SELECTORS.marker));
      this.layers = this.steps.map((step) => ({
        step,
        content: step.querySelector(SELECTORS.content),
        media: step.querySelector(SELECTORS.media),
        isNearViewport: false,
      }));
      this.parallaxEnabled = section.dataset.enableParallax === 'true';
      this.parallaxViewport = window.matchMedia('(min-width: 750px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.parallaxActive = false;
      this.rafId = null;
      this.lenis = null;
      this.lenisTimeoutId = null;
      this.isDestroyed = false;

      this.onScroll = () => this.requestProgressUpdate();
      this.onResize = () => this.requestProgressUpdate();
      this.onLenisScroll = () => this.requestProgressUpdate();
      this.onMediaPreferenceChange = () => {
        if (!this.canUseParallax()) this.resetParallax();
        this.requestProgressUpdate();
      };

      this.section.classList.add('is-initialized');
      this.parallaxViewport.addEventListener('change', this.onMediaPreferenceChange);
      this.reduceMotion.addEventListener('change', this.onMediaPreferenceChange);
      this.bindProgress();
      this.update();
    }

    destroy() {
      this.isDestroyed = true;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.cancelAnimationFrame(this.rafId);
      window.clearTimeout(this.lenisTimeoutId);
      this.unbindLenis();
      this.parallaxViewport.removeEventListener('change', this.onMediaPreferenceChange);
      this.reduceMotion.removeEventListener('change', this.onMediaPreferenceChange);
      this.resetParallax();
    }

    bindProgress() {
      if (!this.timeline) return;

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.bindLenis();
      this.lenisTimeoutId = window.setTimeout(() => this.bindLenis(), 500);
    }

    bindLenis() {
      if (this.lenis || !window.lenis?.on) return;

      this.lenis = window.lenis;
      this.lenis.on('scroll', this.onLenisScroll);
    }

    unbindLenis() {
      if (!this.lenis?.off) {
        this.lenis = null;
        return;
      }

      this.lenis.off('scroll', this.onLenisScroll);
      this.lenis = null;
    }

    requestProgressUpdate() {
      if (this.rafId || this.isDestroyed) return;

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    update() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const activationY = viewportHeight * 0.55;
      const stepRects = this.steps.map((step) => step.getBoundingClientRect());
      const lineRect = this.line?.getBoundingClientRect();
      const markerRects = this.markers.map((marker) => marker.getBoundingClientRect());

      this.updateSteps(activationY, stepRects);
      const rawProgress = this.updateProgress(activationY, lineRect);
      this.updateMarkers(rawProgress, lineRect, markerRects);
      this.updateParallax(viewportHeight, stepRects);
    }

    updateSteps(activationY, stepRects) {
      let activeIndex = -1;

      stepRects.forEach((stepRect, index) => {
        if (stepRect.top <= activationY) activeIndex = index;
      });

      this.steps.forEach((step, index) => {
        step.classList.toggle(VISIBLE_CLASS, index <= activeIndex);
        step.classList.toggle(ACTIVE_CLASS, index === activeIndex);
      });
    }

    updateProgress(activationY, lineRect) {
      if (!lineRect) {
        this.setProgress(0);
        return -1;
      }

      const progress = lineRect.height > 0
        ? (activationY - lineRect.top) / lineRect.height
        : 0;

      this.setProgress(progress);
      return progress;
    }

    setProgress(progress) {
      const clampedProgress = clamp(progress, 0, 1);

      this.section.style.setProperty('--timeline-progress', clampedProgress.toFixed(4));
      this.section.style.setProperty('--timeline-progress-stop', `${(clampedProgress * 100).toFixed(2)}%`);
    }

    updateMarkers(progress, lineRect, markerRects) {
      if (!lineRect || lineRect.height <= 0) {
        this.markers.forEach((marker) => marker.classList.remove(ACTIVE_CLASS));
        return;
      }

      this.markers.forEach((marker, index) => {
        const markerRect = markerRects[index];

        if (!markerRect) return;

        const markerCenter = markerRect.top + markerRect.height / 2;
        const threshold = clamp((markerCenter - lineRect.top) / lineRect.height, 0, 1);

        marker.classList.toggle(ACTIVE_CLASS, progress >= threshold);
      });
    }

    canUseParallax() {
      return this.parallaxEnabled && this.parallaxViewport.matches && !this.reduceMotion.matches && this.layers.length > 0;
    }

    updateParallax(viewportHeight, stepRects) {
      if (!this.canUseParallax()) {
        this.resetParallax();
        return;
      }

      const viewportBuffer = viewportHeight * 0.25;
      const layerUpdates = [];

      this.parallaxActive = true;
      this.layers.forEach((layer, index) => {
        const { content, media } = layer;
        const stepRect = stepRects[index];

        if (!stepRect || stepRect.bottom < -viewportBuffer || stepRect.top > viewportHeight + viewportBuffer) {
          if (layer.isNearViewport) {
            layerUpdates.push({ content, media, imageY: 0, contentY: 0 });
            layer.isNearViewport = false;
          }
          return;
        }

        layer.isNearViewport = true;
        const progress = clamp(
          (viewportHeight - stepRect.top) / (viewportHeight + stepRect.height),
          0,
          1
        );
        const imageY = Math.round((100 - progress * 200) * 100) / 100;
        const contentY = Math.round((-50 + progress * 100) * 100) / 100;

        layerUpdates.push({ content, media, imageY, contentY });
      });

      layerUpdates.forEach(({ content, media, imageY, contentY }) => {
        content?.style.setProperty('--content-parallax-y', `${contentY}px`);
        media?.style.setProperty('--image-parallax-y', `${imageY}px`);
      });
    }

    resetParallax() {
      if (!this.parallaxActive) return;

      this.layers.forEach((layer) => {
        const { content, media } = layer;
        content?.style.setProperty('--content-parallax-y', '0px');
        media?.style.setProperty('--image-parallax-y', '0px');
        layer.isNearViewport = false;
      });
      this.parallaxActive = false;
    }
  }

  const initSections = (root = document) => {
    const sections = root.matches?.(SELECTORS.section)
      ? [root]
      : Array.from(root.querySelectorAll(SELECTORS.section));

    sections.forEach((section) => {
      const existingInstance = instances.get(section);
      if (existingInstance) existingInstance.destroy();

      instances.set(section, new HowWeMakeIt(section));
    });
  };

  const destroySections = (root) => {
    const sections = root.matches?.(SELECTORS.section)
      ? [root]
      : Array.from(root.querySelectorAll(SELECTORS.section));

    sections.forEach((section) => {
      const instance = instances.get(section);

      if (!instance) return;

      instance.destroy();
      instances.delete(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSections());
  } else {
    initSections();
  }

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroySections(event.target));
})();
